import { zodResolver } from "@hookform/resolvers/zod";
import { parseDate } from "@internationalized/date";
import { MinusIcon, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import * as z from "zod";

import {
  baseSchema,
  emailSchema,
  localSchema,
  oidcInputSchema,
  oidcIssuerSchema,
  samlInputSchema,
} from "@blis/shared/auth/schema";
import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import { DatePicker, DatePickerInput } from "@/components/ui/date-picker";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextArea,
  TextField,
} from "@/components/ui/field";
import { Radio, RadioField, RadioGroup } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useConfig } from "@/lib/config";
import type { AuthProvider } from "../../types";
import { AdminForm } from "../AdminForm";
import { Section } from "../Section";

const baseFormSchema = baseSchema.omit({ emailDomains: true }).extend({
  id: z.number().optional(),
  emailDomains: z
    .string()
    .optional()
    .transform((s) => s?.split(/[,\s]+/gm).filter((s) => !!s.trim())),
});

const allSchemas = baseFormSchema.extend({
  config: z.union([samlInputSchema, oidcInputSchema, localSchema, emailSchema]),
});

type FormOutput = z.output<typeof allSchemas>;
type FormInput = z.input<typeof allSchemas>;
type CreateFormOutput = Omit<FormOutput, "id">;
type UpdateFormOutput = FormOutput & { id: number };

function getSchemaForType(type: string | undefined) {
  switch (type) {
    case "SAML":
      return baseFormSchema.extend({ config: samlInputSchema });
    case "LOCAL":
      return baseFormSchema.extend({ config: localSchema });
    case "EMAIL":
      return baseFormSchema.extend({ config: emailSchema });
    default:
      return baseFormSchema.extend({ config: oidcInputSchema });
  }
}

type AuthProviderFormProps =
  | {
      provider?: undefined;
      onSubmit: (values: CreateFormOutput) => Promise<void>;
    }
  | {
      provider: AuthProvider;
      onSubmit: (values: UpdateFormOutput) => Promise<void>;
    };

export function AuthProviderForm(props: AuthProviderFormProps) {
  const { provider } = props;
  const [schema, setSchema] = useState(() =>
    getSchemaForType(provider?.type ?? "OPENID"),
  );
  const providerConfig = provider?.authProviderConfig.reduce(
    (prev, curr) => {
      prev[curr.name] = curr.value ?? "";
      return prev;
    },
    {} as Record<string, string>,
  );

  const methods = useForm<FormInput, unknown, FormOutput>({
    defaultValues: provider
      ? {
          id: provider.id,
          name: provider.name,
          description: provider.description ?? undefined,
          type: provider.type,
          emailDomains: provider.emailDomains?.join(", ") || "",
          isDefault: provider.isDefault,
          config: providerConfig,
        }
      : {
          type: "OPENID",
        },
    resolver: zodResolver(schema as z.ZodType<FormOutput, FormInput>),
  });
  const type = methods.watch("type");

  useEffect(() => {
    setSchema(getSchemaForType(type));
  }, [type]);

  const onFormSubmit: SubmitHandler<FormOutput> = async (values) => {
    try {
      if (props.provider) {
        await props.onSubmit(values as UpdateFormOutput);
      } else {
        const { id, ...rest } = values;
        await props.onSubmit(rest);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <FormProvider {...methods}>
      <AdminForm
        onSubmit={methods.handleSubmit(onFormSubmit)}
        className="relative"
      >
        <BaseFields />
        <ProviderConfig />
        <div className="flex justify-end gap-2">
          <Button
            onPress={() => history.back()}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button type="submit" isPending={methods.formState.isSubmitting}>
            Save
          </Button>
        </div>
      </AdminForm>
    </FormProvider>
  );
}

function BaseFields() {
  const { control } = useFormContext<FormInput>();
  return (
    <>
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState: { error } }) => (
          <TextField {...field} isInvalid={!!error} validationBehavior="aria">
            <Label>Name</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field, fieldState: { error } }) => (
          <TextField {...field} isInvalid={!!error} validationBehavior="aria">
            <Label>Description</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />

      <DefaultCheckbox />
      <DomainInput />
      <TypeSelect />
    </>
  );
}

function DomainInput() {
  const { control, watch } = useFormContext<FormInput>();

  const isDefault = watch("isDefault");

  return isDefault ? null : (
    <Controller
      control={control}
      name="emailDomains"
      render={({ field, fieldState: { error } }) => (
        <TextField {...field} isInvalid={!!error} validationBehavior="aria">
          <Label>Email domains</Label>
          <Input />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function TypeSelect() {
  const { control, getValues } = useFormContext<FormInput>();

  const existingId = getValues("id");

  return (
    <Controller
      control={control}
      name="type"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <RadioGroup
          {...field}
          value={field.value || ""}
          onChange={(val) => field.onChange(val || null)}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Type</Label>
          <Radio value="OPENID" id="oidc" isDisabled={!!existingId}>
            OpenID Connect
          </Radio>
          <Radio value="SAML" id="saml" isDisabled={!!existingId}>
            SAML 2.0
          </Radio>
          <Radio value="LOCAL" id="local" isDisabled={!!existingId}>
            Email/Password
          </Radio>
          <Radio value="EMAIL" id="email" isDisabled={!!existingId}>
            Email magic link / OTP
          </Radio>
          <FieldError>{error?.message}</FieldError>
        </RadioGroup>
      )}
    />
  );
}

function DefaultCheckbox() {
  const { control } = useFormContext<FormInput>();

  return (
    <Controller
      control={control}
      name="isDefault"
      render={({ field, fieldState: { error } }) => (
        <CheckboxField>
          <Checkbox
            isSelected={field.value}
            onChange={(flag) => field.onChange(!!flag)}
            onBlur={field.onBlur}
            name={field.name}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            Default provider
          </Checkbox>
          <Description>
            This will be the default provider for unknown email domains
          </Description>
        </CheckboxField>
      )}
    />
  );
}

function ProviderConfig() {
  const { watch } = useFormContext<FormInput>();
  const type = watch("type");
  return (
    <>
      {type === "OPENID" && <OidcFields />}
      {type === "SAML" && <SamlFields />}
      {type === "LOCAL" && <LocalFields />}
      {type === "EMAIL" && <EmailFields />}
    </>
  );
}

function SamlSignatureAlgorithmRadio() {
  const { control } = useFormContext<FormInput>();
  return (
    <Controller
      control={control}
      name="config.signatureAlgorithm"
      render={({ field, fieldState: { error } }) => (
        <RadioGroup
          id="saml-algorithm"
          value={field.value || ""}
          onChange={(val: string) => field.onChange(val || null)}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Signature algorithm</Label>
          <Radio value="sha1" id="sha1">
            SHA1
          </Radio>
          <Radio value="sha256" id="sha256">
            SHA256
          </Radio>
          <Radio value="sha512" id="sha512">
            SHA512
          </Radio>
          <FieldError>{error?.message}</FieldError>
        </RadioGroup>
      )}
    />
  );
}

function OidcDiscovery() {
  const { setValue } = useFormContext<FormInput>();
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const discover = trpc.admin.auth.discoverOidc.useQuery(
    { url },
    { enabled: false },
  );

  const onImport = async () => {
    setLoading(true);
    const res = await discover.refetch();
    setLoading(false);

    if (res.error) {
      setError(res.error.message);
    } else {
      const parsed = oidcIssuerSchema.safeParse(res.data?.metadata || {});
      if (parsed.success && parsed.data) {
        for (const [key, value] of Object.entries(parsed.data)) {
          // biome-ignore lint/suspicious/noExplicitAny: react-hook-form issues
          setValue(`config.${key}` as any, value);
        }
      } else {
        setError("Unexpected OIDC metadata");
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <TextField
        id="import-oidc"
        value={url}
        onChange={setUrl}
        isInvalid={!!error}
        validationBehavior="aria"
        className="w-full"
      >
        <Label>Import from URL</Label>
        <Input />
        <FieldError>{error}</FieldError>
      </TextField>
      <Button
        className="mt-[24px]"
        type="button"
        onPress={onImport}
        isPending={loading}
        variant="outline"
      >
        Import
      </Button>
    </div>
  );
}

function OidcFields() {
  const {
    register,
    control,
    getValues,
    formState: { errors },
  } = useFormContext<FormInput>();
  const existingId = getValues("id");
  const providerUrl = useProviderUrl();
  return (
    <div className="flex flex-col gap-2">
      <Section.Subtitle>OpenID Connect configuration</Section.Subtitle>
      <TextField value={`${providerUrl}/cb`} isDisabled>
        <Label>Redirect URI</Label>
        <Input />
      </TextField>
      {!existingId && (
        <>
          <OidcDiscovery />
          <Separator className="my-4" />
        </>
      )}
      <Controller
        name="config.issuer"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="oidc-issuer"
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Issuer</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.authorization_endpoint"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="oidc-auth-url"
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Authorization URL</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.token_endpoint"
        control={control}
        render={({ field: { value, ...field }, fieldState: { error } }) => (
          <TextField
            id="oidc-token-url"
            {...field}
            value={value ?? ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Token URL</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.userinfo_endpoint"
        control={control}
        render={({ field: { value, ...field }, fieldState: { error } }) => (
          <TextField
            id="oidc-userinfo-uri"
            {...field}
            value={value ?? ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Userinfo endpoint</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.revocation_endpoint"
        control={control}
        render={({ field: { value, ...field }, fieldState: { error } }) => (
          <TextField
            id="oidc-revocation-uri"
            {...field}
            value={value ?? ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Revocation endpoint</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.end_session_endpoint"
        control={control}
        render={({ field: { value, ...field }, fieldState: { error } }) => (
          <TextField
            id="oidc-end-session-uri"
            {...field}
            value={value ?? ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>End session endpoint</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.jwks_uri"
        control={control}
        render={({ field: { value, ...field }, fieldState: { error } }) => (
          <TextField
            id="oidc-jwks-uri"
            {...field}
            value={value ?? ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>JWKS URI</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.client_id"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="oidc-client-id"
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Client ID</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.client_secret"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="oidc-client-secret"
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Client Secret</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="config.secret_expiration"
        control={control}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <DatePicker
            id="oidc-secret-expiration"
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
            granularity="day"
            value={field.value ? parseDate(field.value) : null}
            onChange={(val) => field.onChange(val?.toString() || null)}
          >
            <Label>Secret expiration</Label>
            <DatePickerInput />
            <FieldError>{error?.message}</FieldError>
          </DatePicker>
        )}
      />
      <div className="flex flex-col gap-2">
        <Section.Subtitle>Options</Section.Subtitle>
        <SSOLogout />
      </div>
    </div>
  );
}

function SamlFields() {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<FormInput>();

  const providerUrl = useProviderUrl();
  return (
    <div className="flex flex-col gap-2">
      <Section.Subtitle>SAML 2.0 configuration</Section.Subtitle>
      <TextField value={`${providerUrl}`} isDisabled>
        <Label>Entity ID</Label>
        <Input />
      </TextField>
      <CustomEntityId />
      <TextField value={`${providerUrl}/cb`} isDisabled>
        <Label>Reply URL</Label>
        <Input />
      </TextField>
      <Controller
        control={control}
        name="config.entryPoint"
        render={({ field, fieldState: { error } }) => (
          <TextField {...field} isInvalid={!!error} validationBehavior="aria">
            <Label>SSO Service URL</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        control={control}
        name="config.idpCert"
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            id="saml-cert"
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Certificate</Label>
            <TextArea rows={10} />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />

      <SamlSignatureAlgorithmRadio />
      <Controller
        control={control}
        name="config.secret_expiration"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <DatePicker
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
            granularity="day"
            value={field.value ? parseDate(field.value) : null}
            onChange={(val) => field.onChange(val?.toString() || null)}
          >
            <Label>Secret expiration</Label>
            <DatePickerInput />
            <FieldError>{error?.message}</FieldError>
          </DatePicker>
        )}
      />
      <div className="flex flex-col gap-2">
        <Section.Subtitle>Options</Section.Subtitle>
        <Controller
          control={control}
          name="config.wantAssertionsSigned"
          render={({ field, fieldState: { error } }) => (
            <Checkbox
              isSelected={field.value === "true"}
              onChange={(flag) => field.onChange(flag ? "true" : "false")}
              onBlur={field.onBlur}
              name={field.name}
              isInvalid={!!error}
            >
              Want assertions signed
            </Checkbox>
          )}
        />
        <Controller
          control={control}
          name="config.wantAuthnResponseSigned"
          render={({ field, fieldState: { error } }) => (
            <Checkbox
              isSelected={field.value === "true"}
              onChange={(flag) => field.onChange(flag ? "true" : "false")}
              onBlur={field.onBlur}
              name={field.name}
              isInvalid={!!error}
            >
              Want response signed
            </Checkbox>
          )}
        />
        <SSOLogout />
      </div>
    </div>
  );
}

function LocalFields() {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormInput>();

  return (
    <div className="flex flex-col gap-2">
      <Section.Subtitle>Local configuration</Section.Subtitle>
      <Controller
        control={control}
        name="config.allowSignup"
        render={({ field, fieldState: { error } }) => (
          <Checkbox
            isSelected={field.value === "true"}
            onChange={(flag) => field.onChange(flag ? "true" : "false")}
            onBlur={field.onBlur}
            name={field.name}
            isInvalid={!!error}
          >
            Allow signups
          </Checkbox>
        )}
      />
    </div>
  );
}

function EmailFields() {
  const { control } = useFormContext<FormInput>();
  return (
    <>
      <div className="flex flex-col gap-2">
        <Section.Subtitle>Email configuration</Section.Subtitle>
      </div>
      <Controller
        control={control}
        name="config.emailType"
        render={({ field, fieldState: { error } }) => (
          <RadioGroup
            {...field}
            value={field.value || ""}
            onChange={(val) => field.onChange(val || null)}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Email type</Label>
            <RadioField>
              <Radio value="MAGIC_LINK" id="magic-link">
                Magic link
              </Radio>
              <Description>Users will receive a link to sign in</Description>
            </RadioField>
            <RadioField>
              <Radio value="OTP" id="otp">
                OTP
              </Radio>
              <Description>
                Users will receive a one-time passcode (OTP) to sign in
              </Description>
            </RadioField>
            <FieldError>{error?.message}</FieldError>
          </RadioGroup>
        )}
      />
    </>
  );
}

function SSOLogout() {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormInput>();

  return (
    <Controller
      control={control}
      name="config.ssoLogout"
      render={({ field, fieldState: { error } }) => (
        <Checkbox
          isSelected={field.value === "true"}
          onChange={(flag) => field.onChange(flag ? "true" : "false")}
          onBlur={field.onBlur}
          name={field.name}
          isInvalid={!!error}
        >
          Single sign-out
        </Checkbox>
      )}
    />
  );
}

function CustomEntityId() {
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<FormInput>();
  const entityId = getValues("config.entityId");
  const [show, setShow] = useState(!!entityId);

  const onClick = () => {
    setValue("config.entityId", "");
    setShow((val) => !val);
  };

  const LabelText = (
    <div className="flex items-center gap-2">
      <span>Custom entity ID</span>
      <Button onPress={onClick} size="icon-sm" variant="ghost">
        <MinusIcon />
      </Button>
    </div>
  );

  return show ? (
    <Controller
      control={control}
      name="config.entityId"
      render={({ field, fieldState: { error } }) => (
        <TextField {...field} isInvalid={!!error} validationBehavior="aria">
          <Label>{LabelText}</Label>
          <Input />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  ) : (
    <div>
      <Button onPress={onClick} variant="ghost" size="sm">
        <PlusIcon />
        Custom entity ID
      </Button>
    </div>
  );
}

function useProviderUrl() {
  const { watch } = useFormContext<FormInput>();
  const name = watch("name");
  const config = useConfig();

  return `${config.apiRootUrl}/auth/provider/${
    name?.toLowerCase().replace(/[\s]+/, "-") || "<name>"
  }`;
}
